// @flow
import _ from 'lodash'
import type { PlacementType } from './PlacementTypeSelect'
import type { FbPlacements } from '../types'

const t = {
  facebook_positions_feed: 'Ленты',
  facebook_positions_right_hand_column: 'Правый столбец',
  facebook_positions_instant_article: 'Моментальные статьи',
  facebook_positions_marketplace: '',
  facebook_positions_story: 'Facebook Истории',
  instagram_positions_stream: 'Лента',
  instagram_positions_story: 'Instagram Истории',
  audience_network_positions_classic: 'Native, Banner and interstitial',
  audience_network_positions_instream_video: 'Вставки в видео',
  audience_network_positions_rewarded_video: 'Видео с бонусами',
  messenger_positions_messenger_home: 'Главная страница Messenger',
  messenger_positions_sponsored_messages: 'Sponsored messages',
  facebook: 'Facebook',
  instagram: 'Instagram',
  audience_network: 'Audience Network',
  messenger: 'Messenger'
}

// platforms
const FACEBOOK = 'facebook'
const INSTAGRAM = 'instagram'
const AUDIENCE_NETWORK = 'audience_network'
const MESSENGER = 'messenger'

// facebook
const FEED = 'feed'
const INSTANT_ARTICLE = 'instant_article'
const RIGHT_HAND_COLUMN = 'right_hand_column'
const FACEBOOK_STORY = 'story'

// instagram
const STREAM = 'stream'
const INSTAGRAM_STORY = 'story'

// audience_network
const CLASSIC = 'classic'
// const INSTREAM_VIDEO = 'instream_video'
const REWARDED_VIDEO = 'rewarded_video'

// messenger
const MESSENGER_HOME = 'messenger_home'
// const SPONSORED_MESSAGES = 'sponsored_messages'

export type ConsumerPlatformType = 'mobile' | 'desktop' | 'all'
export type FbPlatformType = 'facebook' | 'instagram' | 'audience_network' | 'messenger'

const ALL_FACEBOOK_PLACEMENTS: FbPlacements = {
  facebook: [FEED, RIGHT_HAND_COLUMN, INSTANT_ARTICLE, FACEBOOK_STORY],
  instagram: [STREAM, INSTAGRAM_STORY],
  audience_network: [CLASSIC, REWARDED_VIDEO],
  messenger: [MESSENGER_HOME]
}

const DESKTOP = 'desktop'
const PRODUCT_RETARGETING = 'product_retargeting'
const POSTS_PROMOTION = 'posts_promotion'

const PRODUCT_RETARGETING_PLACEMENTS: FbPlacements = {
  facebook: [FEED, RIGHT_HAND_COLUMN, FACEBOOK_STORY],
  instagram: [STREAM, INSTAGRAM_STORY],
  audience_network: [],
  messenger: []
}

const POSTS_PROMOTION_PLACEMENTS: FbPlacements = {
  facebook: [FEED, RIGHT_HAND_COLUMN, INSTANT_ARTICLE],
  instagram: [],
  audience_network: [],
  messenger: []
}

const MULTI_FORMAT_FACEBOOK_PLACEMENTS: FbPlacements = {
  facebook: [FEED, RIGHT_HAND_COLUMN, INSTANT_ARTICLE, FACEBOOK_STORY],
  instagram: [STREAM, INSTAGRAM_STORY],
  audience_network: [],
  messenger: []
}

const TYPES_FACEBOOK_PLACEMENTS: { [PlacementType]: FbPlacements } = {
  promo_post: {
    facebook: [FEED, INSTANT_ARTICLE, FACEBOOK_STORY],
    instagram: [STREAM, INSTAGRAM_STORY],
    audience_network: [],
    messenger: []
  },
  column: {
    facebook: [FEED, RIGHT_HAND_COLUMN, INSTANT_ARTICLE],
    instagram: [STREAM, INSTAGRAM_STORY],
    audience_network: [],
    messenger: []
  },
  multi_format: MULTI_FORMAT_FACEBOOK_PLACEMENTS,
  with_video: MULTI_FORMAT_FACEBOOK_PLACEMENTS,
  carousel: MULTI_FORMAT_FACEBOOK_PLACEMENTS
}

export type FbPlacementPositionRenderData = {|
  position: string,
  label: string,
  isDisabled: boolean,
  isSelected: boolean
|}

export type FbPlacementPlatformRenderData = {|
  title: string,
  platform: string,
  positions: Array<FbPlacementPositionRenderData>
|}

export type FbPlacementRenderData = Array<FbPlacementPlatformRenderData>

function rejectInstantArticleOnNotMobile(
  fbPlacements: FbPlacements,
  platform: ConsumerPlatformType
): FbPlacements {
  if (platform === DESKTOP) {
    return { ...fbPlacements, facebook: _.without(fbPlacements.facebook, INSTANT_ARTICLE) }
  } else {
    return fbPlacements
  }
}

function rejectInstagramOnNotMobile(
  fbPlacements: FbPlacements,
  platform: ConsumerPlatformType
): FbPlacements {
  return platform === DESKTOP ? _.omit(fbPlacements, INSTAGRAM) : fbPlacements
}

function rejectMessengerOnNotMobile(
  fbPlacements: FbPlacements,
  platform: ConsumerPlatformType
): FbPlacements {
  return platform === DESKTOP ? _.omit(fbPlacements, MESSENGER) : fbPlacements
}

function addFeedWhenInstantArticle(fbPlacements: FbPlacements): FbPlacements {
  if (_.includes(fbPlacements.facebook, INSTANT_ARTICLE)) {
    return { ...fbPlacements, facebook: _.uniq([...(fbPlacements.facebook || []), FEED]) }
  } else {
    return fbPlacements
  }
}

function addInstagramStoriesWhenFbStories(
  fbPlacements: FbPlacements,
  platform: ConsumerPlatformType
): FbPlacements {
  if (platform === DESKTOP) return fbPlacements
  if (_.includes(fbPlacements.facebook, FACEBOOK_STORY)) {
    return {
      ...fbPlacements,
      instagram: _.uniq([...(fbPlacements.instagram || []), INSTAGRAM_STORY])
    }
  } else {
    return fbPlacements
  }
}

function addFeedWhenAudienceNetwork(fbPlacements: FbPlacements): FbPlacements {
  if (!_.isEmpty(fbPlacements.audience_network)) {
    return { ...fbPlacements, facebook: _.uniq([...(fbPlacements.facebook || []), FEED]) }
  } else {
    return fbPlacements
  }
}

function filterPlatforms(audienceType: string): FbPlacements {
  if (audienceType === PRODUCT_RETARGETING) {
    return _.omit(ALL_FACEBOOK_PLACEMENTS, [AUDIENCE_NETWORK, MESSENGER])
  }

  if (audienceType === POSTS_PROMOTION) {
    return _.omit(ALL_FACEBOOK_PLACEMENTS, [INSTAGRAM, AUDIENCE_NETWORK, MESSENGER])
  }

  return ALL_FACEBOOK_PLACEMENTS
}

function isDisabled(
  fbPlacements: FbPlacements,
  platform: FbPlatformType,
  position: string,
  selectedPlatform: ConsumerPlatformType
): boolean {
  if (
    platform === FACEBOOK &&
    position === FEED &&
    (_.includes(fbPlacements.facebook || [], INSTANT_ARTICLE) ||
      !_.isEmpty(fbPlacements.audience_network))
  ) {
    return true
  }

  if (selectedPlatform === DESKTOP) {
    if (platform === FACEBOOK && position === INSTANT_ARTICLE) return true

    if (platform === INSTAGRAM) return true

    if (platform === MESSENGER) return true
  }

  if (
    platform === INSTAGRAM &&
    position === INSTAGRAM_STORY &&
    _.includes(fbPlacements.facebook || [], FACEBOOK_STORY)
  ) {
    return true
  }

  return false
}

function isPlacementSelected(
  fbPlacements: FbPlacements,
  platform: FbPlatformType,
  position: string
) {
  return _.includes(fbPlacements[platform], position)
}

function makeFbPlacementsConsistent(fbPlacement: FbPlacements, platform: ConsumerPlatformType) {
  let pipe = rejectInstantArticleOnNotMobile(fbPlacement, platform)
  pipe = rejectInstagramOnNotMobile(pipe, platform)
  pipe = rejectMessengerOnNotMobile(pipe, platform)
  pipe = addFeedWhenInstantArticle(pipe)
  pipe = addInstagramStoriesWhenFbStories(pipe, platform)

  return addFeedWhenAudienceNetwork(pipe)
}

function getDefaultFbPlacements(
  placementType: PlacementType,
  audienceType: string,
  platform: ConsumerPlatformType
): FbPlacements {
  if (audienceType === PRODUCT_RETARGETING) {
    return PRODUCT_RETARGETING_PLACEMENTS
  }

  if (audienceType === POSTS_PROMOTION) {
    return POSTS_PROMOTION_PLACEMENTS
  }

  return makeFbPlacementsConsistent(TYPES_FACEBOOK_PLACEMENTS[placementType], platform)
}

function renderData(
  fbPlacements: FbPlacements,
  audienceType: string,
  selectedPlatform: ConsumerPlatformType
): FbPlacementRenderData {
  return _.map((filterPlatforms(audienceType): FbPlacements), (positions: any, platform: any) => {
    return {
      title: t[platform],
      platform,
      positions: positions.map((position) => ({
        position,
        label: t[`${platform}_positions_${position}`],
        isSelected: isPlacementSelected(fbPlacements, platform, position),
        isDisabled: isDisabled(fbPlacements, platform, position, selectedPlatform)
      }))
    }
  })
}

export { makeFbPlacementsConsistent, getDefaultFbPlacements, renderData }
